import type { PrimaryCategory, RegistrationPolicy } from "../../api/registration";
import type { RegistrationPaperEntry } from "../../utils/registrationPapers";

export type RegistrationFieldErrors = Partial<
  Record<
    | "firstName"
    | "lastName"
    | "companyName"
    | "country"
    | "state"
    | "memberId"
    | "papers"
    | "additionalPaperCount"
    | "banquetQuantity"
    | "primaryCategory"
    | "addOnRegistrants",
    string
  >
> & {
  paperFields?: Partial<
    Record<
      number,
      { paperId?: string; paperTitle?: string; paperCount?: string }
    >
  >;
  /** Keyed by paper index, then by person index within that paper. */
  addOnFields?: Partial<
    Record<
      number,
      Partial<Record<number, { name?: string; email?: string; memberId?: string }>>
    >
  >;
};

const NAME_PATTERN = /^[\p{L}\p{M}'.-]+$/u;
const MEMBER_ID_PATTERN = /^[A-Za-z0-9-]+$/;

function isIeeeTier(tier: import("../../api/registration").MembershipTier): boolean {
  return tier !== "non_ieee_member";
}

export interface Step1Values {
  firstName: string;
  lastName: string;
  companyName: string;
  country: string;
  state: string;
  membershipTier: import("../../api/registration").MembershipTier;
  memberId: string;
  countrySelectedFromList: boolean;
  isUsCountry: boolean;
}

export function validateStep1(values: Step1Values): RegistrationFieldErrors {
  const errors: RegistrationFieldErrors = {};

  const firstName = values.firstName.trim();
  if (!firstName) {
    errors.firstName = "First name is required.";
  } else if (firstName.length > 100) {
    errors.firstName = "First name must be 100 characters or fewer.";
  } else if (values.firstName !== firstName) {
    errors.firstName = "Remove leading or trailing spaces.";
  } else if (!NAME_PATTERN.test(firstName)) {
    errors.firstName = "Use letters, spaces, hyphens, periods, or apostrophes only.";
  }

  const lastName = values.lastName.trim();
  if (!lastName) {
    errors.lastName = "Last name is required.";
  } else if (lastName.length > 100) {
    errors.lastName = "Last name must be 100 characters or fewer.";
  } else if (values.lastName !== lastName) {
    errors.lastName = "Remove leading or trailing spaces.";
  } else if (!NAME_PATTERN.test(lastName)) {
    errors.lastName = "Use letters, spaces, hyphens, periods, or apostrophes only.";
  }

  const companyName = values.companyName.trim();
  if (companyName.length > 200) {
    errors.companyName = "Company name must be 200 characters or fewer.";
  } else if (values.companyName !== companyName && values.companyName) {
    errors.companyName = "Remove leading or trailing spaces.";
  }

  if (!values.country.trim()) {
    errors.country = "Please select a country from the list.";
  } else if (!values.countrySelectedFromList) {
    errors.country = "Choose a country from the search results.";
  }

  const state = values.state.trim();
  if (values.isUsCountry && values.country && !state) {
    errors.state = "Please select a US state.";
  } else if (state.length > 100) {
    errors.state = "State must be 100 characters or fewer.";
  } else if (values.state !== state && values.state) {
    errors.state = "Remove leading or trailing spaces.";
  }

  if (isIeeeTier(values.membershipTier)) {
    const memberId = values.memberId.trim();
    if (!memberId) {
      errors.memberId = "IEEE Member ID is required.";
    } else if (/\s/.test(memberId)) {
      errors.memberId = "Member ID must not contain spaces.";
    } else if (!MEMBER_ID_PATTERN.test(memberId)) {
      errors.memberId = "Use letters, numbers, and hyphens only.";
    } else if (memberId.length > 50) {
      errors.memberId = "Member ID must be 50 characters or fewer.";
    }
  }

  return errors;
}

export function validateBanquetQuantity(qty: number): string | null {
  if (!Number.isInteger(qty) || qty < 0) {
    return "Enter a valid number of banquet tickets.";
  }
  if (qty > 20) {
    return "Maximum 20 banquet tickets per registration.";
  }
  return null;
}

export interface Step2Values {
  primaryCategory: PrimaryCategory;
  papers: RegistrationPaperEntry[];
  participationFormat: "in_person" | "virtual" | "on_demand";
  moreThanOnePaper: boolean;
  additionalPaperCount: number;
  expectedPaperCount: number;
  registrationPolicy?: RegistrationPolicy;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateStep2(values: Step2Values): RegistrationFieldErrors {
  const errors: RegistrationFieldErrors = {};
  const isPaperPlusAddon = values.registrationPolicy === "paper_plus_addon";

  if (values.primaryCategory !== "full_conference") {
    return errors;
  }

  if (isPaperPlusAddon) {
    if (values.papers.length === 0) {
      errors.papers =
        "At least one paper is required for Full Conference registration.";
    }
  } else if (values.papers.length !== values.expectedPaperCount) {
    errors.papers = "Please complete all paper entries.";
  }

  const paperFieldErrors: NonNullable<RegistrationFieldErrors["paperFields"]> =
    {};
  values.papers.forEach((paper, index) => {
    const paperId = paper.paperId.trim();
    const paperTitle = paper.paperTitle.trim();
    const rawPages = paper.paperCount.trim();
    const fieldErrors: {
      paperId?: string;
      paperTitle?: string;
      paperCount?: string;
    } = {};

    if (!paperId) {
      fieldErrors.paperId = "Paper ID is required.";
    } else if (/\s/.test(paperId)) {
      fieldErrors.paperId = "Paper ID must not contain spaces.";
    } else if (!MEMBER_ID_PATTERN.test(paperId)) {
      fieldErrors.paperId = "Use letters, numbers, and hyphens only.";
    } else if (paperId.length > 100) {
      fieldErrors.paperId = "Paper ID must be 100 characters or fewer.";
    }

    if (!paperTitle) {
      fieldErrors.paperTitle = "Paper title is required.";
    } else if (paperTitle.length > 500) {
      fieldErrors.paperTitle = "Paper title must be 500 characters or fewer.";
    }

    if (!isPaperPlusAddon) {
      if (!rawPages) {
        fieldErrors.paperCount = "Paper page count is required.";
      } else if (!/^\d+$/.test(rawPages)) {
        fieldErrors.paperCount = "Enter a whole number of pages.";
      } else {
        const pages = parseInt(rawPages, 10);
        if (pages < 1) {
          fieldErrors.paperCount = "Paper must be at least 1 page.";
        } else if (pages > 500) {
          fieldErrors.paperCount = "Paper page count must be 500 or fewer.";
        }
      }
    }

    if (Object.keys(fieldErrors).length > 0) {
      paperFieldErrors[index] = fieldErrors;
    }
  });

  if (Object.keys(paperFieldErrors).length > 0) {
    errors.paperFields = paperFieldErrors;
    errors.papers = "Please complete all paper details.";
  }

  if (isPaperPlusAddon) {
    const addOnFields: NonNullable<RegistrationFieldErrors["addOnFields"]> = {};
    let hasAddOnError = false;

    values.papers.forEach((paper, paperIndex) => {
      const addOns = paper.addOnRegistrants ?? [];
      if (addOns.length > 10) {
        errors.addOnRegistrants = "Maximum 10 additional people per paper.";
        hasAddOnError = true;
      }
      const perPaperFields: Partial<
        Record<number, { name?: string; email?: string; memberId?: string }>
      > = {};
      addOns.forEach((person, personIndex) => {
        const name = person.name.trim();
        const email = person.email.trim();
        const tier = person.membershipTier;
        const row: { name?: string; email?: string; memberId?: string } = {};
        if (!name) {
          row.name = "Name is required.";
        } else if (!NAME_PATTERN.test(name)) {
          row.name = "Use letters, spaces, hyphens, periods, or apostrophes only.";
        }
        if (!email) {
          row.email = "Email is required.";
        } else if (!EMAIL_PATTERN.test(email)) {
          row.email = "Enter a valid email address.";
        }
        if (tier && tier !== "non_ieee_member") {
          const memberId = (person.memberId ?? "").trim();
          if (!memberId) {
            row.memberId = "IEEE Member ID is required.";
          } else if (/\s/.test(memberId)) {
            row.memberId = "Member ID must not contain spaces.";
          } else if (!MEMBER_ID_PATTERN.test(memberId)) {
            row.memberId = "Use letters, numbers, and hyphens only.";
          } else if (memberId.length > 50) {
            row.memberId = "Member ID must be 50 characters or fewer.";
          }
        }
        if (Object.keys(row).length > 0) perPaperFields[personIndex] = row;
      });
      if (Object.keys(perPaperFields).length > 0) {
        addOnFields[paperIndex] = perPaperFields;
        hasAddOnError = true;
      }
    });

    if (hasAddOnError) {
      if (Object.keys(addOnFields).length > 0) errors.addOnFields = addOnFields;
      errors.addOnRegistrants =
        errors.addOnRegistrants ?? "Please complete all additional person details.";
    }
    return errors;
  }

  if (values.moreThanOnePaper) {
    if (
      !Number.isInteger(values.additionalPaperCount) ||
      values.additionalPaperCount < 1
    ) {
      errors.additionalPaperCount =
        "Select how many additional papers you are registering.";
    } else if (values.additionalPaperCount > 10) {
      errors.additionalPaperCount = "Maximum 10 additional papers per registration.";
    } else if (values.participationFormat !== "in_person") {
      errors.additionalPaperCount =
        "Additional papers are only available for in-person full conference registration.";
    } else if (values.papers.length !== 1 + values.additionalPaperCount) {
      errors.papers = "Please complete all paper entries.";
    }
  } else if (values.papers.length !== 1) {
    errors.papers = "One paper is required for Full Conference registration.";
  }

  return errors;
}

type RegistrationStringFieldKey = Exclude<
  keyof RegistrationFieldErrors,
  "paperFields" | "addOnFields"
>;

export function firstFieldError(
  errors: RegistrationFieldErrors,
): string | null {
  if (errors.addOnFields) {
    for (const paperFields of Object.values(errors.addOnFields)) {
      if (!paperFields) continue;
      for (const row of Object.values(paperFields)) {
        if (!row) continue;
        if (row.name) return row.name;
        if (row.email) return row.email;
        if (row.memberId) return row.memberId;
      }
    }
  }
  if (errors.paperFields) {
    for (const field of Object.values(errors.paperFields)) {
      if (!field) continue;
      if (field.paperId) return field.paperId;
      if (field.paperTitle) return field.paperTitle;
      if (field.paperCount) return field.paperCount;
    }
  }
  const keys = Object.keys(errors).filter(
    (k): k is RegistrationStringFieldKey =>
      k !== "paperFields" && k !== "addOnFields",
  );
  if (keys.length === 0) return null;
  return errors[keys[0]!] ?? null;
}
